import { PencilIcon, PlusIcon } from "@heroicons/react/solid";
import { isEmpty } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import InfoItems from "../../../components/InfoItems";
import { useContacts } from "../../../context/contacts";
import { formatDateToLocale } from "../../../utils/dates";
import { Contact, PaymentMethod } from "../../../utils/types";
import MemberCell from "../../Workspace/common/MemberCell";
import PaymentMethodComponent from "../../Workspace/common/PaymentMethodComponent";
import PaymentMethodFormSidebar, {
  EditPaymentMethodConfig
} from "../../Workspace/ContactsData/PaymentMethodFormSidebar";

const ContactInfoTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { contacts, currentContact, addPaymentMethod } = useContacts();
  const [editPaymentMethodConfig, setEditPaymentMethodConfig] =
    useState<EditPaymentMethodConfig>({ show: false });

  const contact = useMemo(
    () =>
      currentContact ||
      (props.match.params.id ? contacts[props.match.params.id] : undefined),
    [contacts, currentContact, props.match.params.id]
  );

  const renderPaymentMethods = useMemo(
    () => (
      <div className="mt-2 flex flex-col w-full shadow overflow-hidden sm:rounded-lg">
        <div className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
          {contact?.payment_methods.map((paymentMethod, paymentIndex) => (
            <div
              key={paymentIndex}
              className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3 group"
            >
              <div className="flex-1 flex-col min-w-0 focus:outline-none">
                <PaymentMethodComponent paymentMethod={paymentMethod} />
              </div>
              <button
                className="inline-flex rounded-md p-1.5 focus:outline-none text-hotspotty-400 hover:bg-hotspotty-100 dark:text-white dark:hover:text-gray-800 disabled:opacity-50 disabled:cursor-default"
                type="button"
                onClick={() => {
                  setEditPaymentMethodConfig({
                    show: true,
                    contactLinkedUuid: contact?.uuid,
                    parentForm: "contact",
                    paymentMethod
                  });
                }}
              >
                <PencilIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      </div>
    ),
    [contact?.payment_methods, contact?.uuid]
  );

  const getInfoItems = useCallback(
    ({
      created_at,
      updated_at,
      created_by,
      updated_by,
      email,
      phone,
      vat,
      payment_methods
    }: Contact) => [
      {
        label: "Added",
        value: (
          <MemberCell
            memberUuid={created_by}
            subtitle={formatDateToLocale(created_at)}
          />
        )
      },
      {
        label: "Updated",
        value: updated_by ? (
          <MemberCell
            memberUuid={updated_by}
            subtitle={formatDateToLocale(updated_at)}
          />
        ) : null
      },
      { label: "Email", value: email },
      { label: "Phone", value: phone },
      {
        label: (
          <div className="mb-2 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-500">
              Payment methods
            </h3>
            <div
              onClick={() => {
                setEditPaymentMethodConfig({
                  show: true,
                  contactLinkedUuid: contact?.uuid,
                  onSuccess: async (paymentMethod: PaymentMethod) => {
                    if (!contact) return;

                    await addPaymentMethod(paymentMethod, contact);
                  }
                });
              }}
              className="flex items-center text-sm text-gray-500 font-medium dark:text-white cursor-pointer"
            >
              <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
              Add payment method
            </div>
          </div>
        ),
        value: isEmpty(payment_methods) ? null : renderPaymentMethods
      },
      { label: "VAT", value: vat }
    ],
    [addPaymentMethod, contact, renderPaymentMethods]
  );

  return (
    <>
      <PaymentMethodFormSidebar
        config={editPaymentMethodConfig}
        setConfig={setEditPaymentMethodConfig}
      />

      <InfoItems infoItems={contact ? getInfoItems(contact) : []} />
    </>
  );
};

export default ContactInfoTab;
